import React, { useState } from 'react';
import { LogIn, UserPlus, Eye, EyeOff } from 'lucide-react';
import phokiiLogo from '../assets/phokii.png';

export default function Auth({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  // 'auth' | 'forgot' | 'reset' — arriving with ?reset=TOKEN opens the reset form
  const [resetToken] = useState(() => new URLSearchParams(window.location.search).get('reset') || '');
  const [view, setView] = useState(() => resetTokenFromUrl() ? 'reset' : 'auth');
  function resetTokenFromUrl() { return new URLSearchParams(window.location.search).get('reset'); }

  async function postJson(endpoint, body) {
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Request failed');
    return data;
  }

  async function handleForgot(e) {
    e.preventDefault();
    setError(''); setSuccess('');
    try {
      const data = await postJson('/api/forgot-password', { email });
      setSuccess(data.message);
    } catch (err) { setError(err.message); }
  }

  async function handleReset(e) {
    e.preventDefault();
    setError(''); setSuccess('');
    try {
      const data = await postJson('/api/reset-password', { token: resetToken, password });
      window.history.replaceState({}, '', window.location.pathname);
      setSuccess(data.message);
      setView('auth');
      setPassword('');
    } catch (err) { setError(err.message); }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      const endpoint = isSignUp ? '/api/register' : '/api/login';
      const body = isSignUp
        ? { email, password, name: name || email, inviteCode }
        : { email, password };
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || (isSignUp ? 'Registration failed' : 'Login failed'));
      }
      if (isSignUp) {
        setSuccess('Account created. You can now sign in.');
        setIsSignUp(false);
        setPassword('');
        setName('');
      } else {
        const { user, token } = await res.json();
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        onLogin(user, token);
      }
    } catch (err) {
      setError(err.message);
    }
  }

  // ── Forgot / reset password views ──
  if (view === 'forgot' || view === 'reset') {
    const isForgot = view === 'forgot';
    return (
      <div className="min-h-screen flex items-center justify-center p-10" style={{ fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif', background: '#f8fafc' }}>
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-10">
            <img src={phokiiLogo} alt="Phōkii" className="h-8 w-auto" />
            <span className="text-xl font-bold text-gray-900 tracking-tight">Phōkii</span>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">{isForgot ? 'Reset your password' : 'Choose a new password'}</h2>
          <p className="text-sm text-gray-500 mb-8">
            {isForgot ? "Enter your email and we'll send a reset link." : 'Minimum 8 characters.'}
          </p>
          <form onSubmit={isForgot ? handleForgot : handleReset} className="space-y-4">
            {error && <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">{error}</div>}
            {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">{success}</div>}
            {isForgot ? (
              <input
                type="email" required autoFocus placeholder="you@example.com"
                value={email} onChange={e => setEmail(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            ) : (
              <input
                type="password" required autoFocus placeholder="New password" minLength={8}
                value={password} onChange={e => setPassword(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            )}
            <button type="submit" className="w-full py-2.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition-colors">
              {isForgot ? 'Send reset link' : 'Set new password'}
            </button>
            <p className="text-center">
              <button type="button" onClick={() => { setView('auth'); setError(''); setSuccess(''); }} className="text-xs text-gray-400 hover:text-gray-600">
                Back to sign in
              </button>
            </p>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif', background: '#f8fafc' }}>

      {/* ── Left — Marketing (sign-up only) ─────────────────────── */}
      {isSignUp && <div className="flex-1 flex flex-col justify-center px-16 py-16 bg-white border-r border-gray-100">
        <div className="max-w-lg">

          {/* Logo */}
          <div className="flex items-center gap-3 mb-14">
            <img src={phokiiLogo} alt="Phōkii" className="h-8 w-auto" />
            <span className="text-xl font-bold text-gray-900 tracking-tight">Phōkii</span>
          </div>

          {/* Headline — the door's own promise, nothing else. This page was
              still selling the retired PM tool ("kanban", "In Progress",
              "Feature", "Press K") the day before strangers arrive: UX-1. */}
          <h1 className="text-4xl font-bold text-gray-900 leading-tight mb-4">
            Where the work lives.
          </h1>
          <p className="text-lg text-gray-500 mb-12 leading-relaxed">
            Every project on one wall — what’s moving, what’s waiting, what finished today. Star the few things that matter and the app opens to just those.
          </p>

          {/* Features — only what a new account actually meets, in the words
              the product itself uses. */}
          <div className="space-y-8">
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-blue-600 text-sm font-bold">1</span>
              </div>
              <div>
                <p className="font-semibold text-gray-800 text-sm">Every project, one strip</p>
                <p className="text-gray-500 text-sm mt-0.5">The wall shows each project as a strip — in motion, to do, done today. Same width always; your hand sets the order, left is priority.</p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
              </div>
              <div>
                <p className="font-semibold text-gray-800 text-sm">An action is a page, not a checkbox</p>
                <p className="text-gray-500 text-sm mt-0.5">Real notes, a checklist, the next step, and the two dates that matter — on the action itself, not in a separate doc that gets lost.</p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-orange-500 font-bold text-sm">✦</span>
              </div>
              <div>
                <p className="font-semibold text-gray-800 text-sm">The star is yours</p>
                <p className="text-gray-500 text-sm mt-0.5">✦ marks what matters today — you put it there, and Phōkii never decides for you. No streaks, no scores, nothing counting the days you were away.</p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7" /></svg>
              </div>
              <div>
                <p className="font-semibold text-gray-800 text-sm">Plans written for weeks, not tonight</p>
                <p className="text-gray-500 text-sm mt-0.5">Four hundred written plans across twenty subjects, ready to take and rewrite. A missed day costs nothing, because nothing is keeping the account.</p>
              </div>
            </div>
          </div>

          {/* Structure hint */}
          <div className="mt-12 flex items-center gap-3 text-xs text-gray-400">
            <span className="px-2 py-1 bg-gray-100 rounded font-medium text-gray-600">Goal</span>
            <span>→</span>
            <span className="px-2 py-1 bg-gray-100 rounded font-medium text-gray-600">Project</span>
            <span>→</span>
            <span className="px-2 py-1 bg-gray-100 rounded font-medium text-gray-600">Action</span>
            <span className="ml-2">Every action belongs to something bigger.</span>
          </div>
        </div>
      </div>}

      {/* ── Right — Login form ─────────────────────────────────────── */}
      <div className={isSignUp ? "w-96 flex items-center justify-center p-10" : "flex-1 flex items-center justify-center p-10"}>
        <div className={isSignUp ? "w-full" : "w-full max-w-sm"}>
          {!isSignUp && (
            <div className="flex items-center gap-3 mb-10">
              <img src={phokiiLogo} alt="Phōkii" className="h-8 w-auto" />
              <span className="text-xl font-bold text-gray-900 tracking-tight">Phōkii</span>
            </div>
          )}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900">
              {isSignUp ? 'Create account' : 'Welcome back'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {isSignUp ? 'Start connecting your daily work to what matters.' : 'Sign in to your workspace.'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}
            {success && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                {success}
              </div>
            )}

            {isSignUp && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Name <span className="text-gray-400 font-normal">(optional)</span></label>
                <input
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
              <input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full px-3 py-2.5 pr-10 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {isSignUp && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Invite code</label>
                <input
                  type="text"
                  placeholder="From your invite"
                  value={inviteCode}
                  onChange={e => setInviteCode(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-400 mt-1">Phōkii is in private beta — ask Paul for a code.</p>
              </div>
            )}

            <button
              type="submit"
              className="w-full py-2.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center gap-2 mt-2"
            >
              {isSignUp ? <UserPlus className="w-4 h-4" /> : <LogIn className="w-4 h-4" />}
              {isSignUp ? 'Create account' : 'Sign in'}
            </button>

            {!isSignUp && (
              <p className="text-center">
                <button
                  type="button"
                  onClick={() => { setView('forgot'); setError(''); setSuccess(''); }}
                  className="text-xs text-gray-400 hover:text-gray-600"
                >
                  Forgot your password?
                </button>
              </p>
            )}
          </form>

          <p className="text-center text-sm text-gray-500 mt-6">
            {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button
              onClick={() => { setIsSignUp(!isSignUp); setError(''); setSuccess(''); setPassword(''); setName(''); }}
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              {isSignUp ? 'Sign in' : 'Create one'}
            </button>
          </p>
          <p className="text-center mt-3">
            <a href="/welcome.html" className="text-xs text-gray-400 hover:text-gray-600">What is Phōkii?</a>
          </p>
        </div>
      </div>

    </div>
  );
}
